// Phase 32 — Share Card modal (web).
//
// Opens from the Deal Detail page when the deal is owned. Renders a live
// preview of the PNG share-card pulled from the backend, with size toggle
// (square / story), Download, Copy link, and Web Share API integration.
//
// Bilingual via i18next. All interactive controls have data-testid for
// automated coverage.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Download, Link2, Share2, Loader2, Sparkles,
} from "lucide-react";

const SIZES = [
  { key: "square", label_en: "Square (1:1)", label_es: "Cuadrado (1:1)" },
  { key: "story",  label_en: "Story (9:16)", label_es: "Historia (9:16)" },
];

export default function ShareCardModal({ open, onOpenChange, deal }) {
  const { i18n } = useTranslation();
  const isSpanish = (i18n.language || "en").toLowerCase().startsWith("es");
  const locale = isSpanish ? "es" : "en";

  const STR = useMemo(() => isSpanish ? {
    title: "Comparte tu hito",
    subtitle: "Genera una tarjeta lista para LinkedIn, X, WhatsApp o iMessage.",
    download: "Descargar PNG",
    copyLink: "Copiar enlace",
    share: "Compartir…",
    linkCopied: "Enlace copiado",
    downloadFailed: "No se pudo descargar.",
    shareFailed: "No se pudo abrir el panel de compartir.",
    notSupported: "Compartir nativo no disponible — usa Descargar.",
    loading: "Renderizando tu tarjeta…",
  } : {
    title: "Share your milestone",
    subtitle: "Generate a card ready for LinkedIn, X, WhatsApp, or iMessage.",
    download: "Download PNG",
    copyLink: "Copy link",
    share: "Share…",
    linkCopied: "Link copied",
    downloadFailed: "Could not download.",
    shareFailed: "Could not open share sheet.",
    notSupported: "Native share unavailable — use Download.",
    loading: "Rendering your card…",
  }, [isSpanish]);

  const [size, setSize] = useState("square");
  const [blobUrl, setBlobUrl] = useState(null);
  const [pngBlob, setPngBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchTokenRef = useRef(0);

  // Pull a fresh PNG every time `open` flips true OR the size toggles.
  useEffect(() => {
    if (!open || !deal?.id) return undefined;
    const token = ++fetchTokenRef.current;
    setLoading(true);
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    api.get(`/deals/${deal.id}/share-card.png`, {
      params: { size, locale },
      responseType: "blob",
    })
      .then((res) => {
        if (token !== fetchTokenRef.current) return;
        const blob = res.data;
        setPngBlob(blob);
        setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        if (token === fetchTokenRef.current) toast.error(STR.downloadFailed);
      })
      .finally(() => {
        if (token === fetchTokenRef.current) setLoading(false);
      });
    return () => { /* token bump handles staleness */ };
  }, [open, deal?.id, size, locale, STR.downloadFailed]);

  // Cleanup object URL on unmount or close.
  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  const onDownload = () => {
    if (!pngBlob) return;
    const url = URL.createObjectURL(pngBlob);
    const a = document.createElement("a");
    const addr = (deal?.property?.address || "deal").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    a.href = url;
    a.download = `runidea-${addr}-${size}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const onCopyLink = async () => {
    const ref = deal?.referral_link || `${window.location.origin}/?ref=${deal?.id || ""}`;
    try {
      await navigator.clipboard.writeText(ref);
      toast.success(STR.linkCopied);
    } catch {
      toast.error(STR.downloadFailed);
    }
  };

  const onNativeShare = async () => {
    if (!pngBlob) return;
    const fileName = `runidea-${size}.png`;
    const file = new File([pngBlob], fileName, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: isSpanish ? "Mi propiedad en RunIdea" : "My RunIdea milestone",
          text: isSpanish ? "¡Acabo de cerrar!" : "Just closed a deal!",
        });
      } catch (e) {
        if (e?.name !== "AbortError") toast.error(STR.shareFailed);
      }
    } else {
      toast.error(STR.notSupported);
    }
  };

  const previewClass = size === "square"
    ? "aspect-square w-full max-w-md"
    : "aspect-[9/16] w-full max-w-xs";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="share-card-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" /> {STR.title}
          </DialogTitle>
          <DialogDescription>{STR.subtitle}</DialogDescription>
        </DialogHeader>

        {/* Size toggle */}
        <div className="flex items-center justify-center gap-2" data-testid="share-card-size-toggle">
          {SIZES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSize(s.key)}
              data-testid={`share-card-size-${s.key}`}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                size === s.key
                  ? "border-indigo-600 bg-indigo-600 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {isSpanish ? s.label_es : s.label_en}
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className="flex justify-center" data-testid="share-card-preview-wrap">
          <div className={`relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50 ${previewClass}`}>
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                <span className="ms-2 text-sm text-slate-600">{STR.loading}</span>
              </div>
            )}
            {blobUrl && (
              <img
                src={blobUrl}
                alt="Share card"
                data-testid="share-card-preview-img"
                className="h-full w-full object-contain"
              />
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={onCopyLink}
            disabled={loading}
            data-testid="share-card-copy-link"
            className="flex-1"
          >
            <Link2 className="me-2 h-3.5 w-3.5" /> {STR.copyLink}
          </Button>
          <Button
            variant="outline"
            onClick={onNativeShare}
            disabled={!pngBlob || loading}
            data-testid="share-card-native-share"
            className="flex-1"
          >
            <Share2 className="me-2 h-3.5 w-3.5" /> {STR.share}
          </Button>
          <Button
            onClick={onDownload}
            disabled={!pngBlob || loading}
            data-testid="share-card-download"
            className="flex-1 bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <Download className="me-2 h-3.5 w-3.5" /> {STR.download}
          </Button>
        </DialogFooter>

        {/* Tiny utility line so users know where the link points */}
        <p className="text-center text-[11px] text-slate-400" data-testid="share-card-help">
          {API_BASE.replace(/\/api$/, "")} · share-card.png
        </p>
      </DialogContent>
    </Dialog>
  );
}
